import { useRef, useState } from 'react';

import { AlertCircle, Loader2, Paperclip, Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { formatBytes } from '@/lib/utils';

import { PendingAttachment } from './useAttachmentUpload';

interface Props {
    attachments: PendingAttachment[];
    onFilesAdded: (files: File[]) => void;
    onRemove: (id: string) => void;
    /** Second line of the upload card; when omitted the compact paperclip prompt is shown. */
    hint?: string;
}

export function AttachmentDropZone({
    attachments,
    onFilesAdded,
    onRemove,
    hint,
}: Props) {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="space-y-2" data-testid="AttachmentDropZone">
            <div
                data-testid="AttachmentDropZone__dropArea"
                role="button"
                tabIndex={0}
                className={`border-2 border-dashed rounded-lg text-sm cursor-pointer transition-colors ${
                    hint ? 'p-4 text-left' : 'p-4 text-center'
                } ${
                    isDragging
                        ? 'border-primary bg-slate-50'
                        : 'border-slate-300 hover:border-primary-light'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const files = Array.from(e.dataTransfer.files);
                    if (files.length > 0) onFilesAdded(files);
                }}
            >
                <input
                    ref={fileInputRef}
                    data-testid="AttachmentDropZone__input"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) onFilesAdded(files);
                        e.target.value = '';
                    }}
                />
                <DropPrompt hint={hint} />
            </div>

            {attachments.length > 0 && (
                <ul className="space-y-1">
                    {attachments.map((attachment) => (
                        <li
                            key={attachment.id}
                            data-testid="AttachmentDropZone__item"
                            className="flex items-center gap-2 rounded-md border border-slate-200 px-2 py-2 text-sm"
                        >
                            {attachment.status === 'uploading' ? (
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                            ) : attachment.status === 'error' ? (
                                <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
                            ) : (
                                <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                            <span className="flex-1 truncate">
                                {attachment.fileName}
                            </span>
                            <span className="text-xs text-muted-foreground">
                                {attachment.status === 'uploading'
                                    ? 'Uploading…'
                                    : attachment.status === 'error'
                                      ? 'Failed to upload'
                                      : formatBytes(attachment.sizeBytes)}
                            </span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => onRemove(attachment.id)}
                                testId="AttachmentDropZone__remove"
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function DropPrompt({ hint }: { hint?: string }) {
    if (hint === undefined) {
        return (
            <>
                <Paperclip className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
                <p className="text-muted-foreground">
                    Drag & drop files here, or click to browse
                </p>
            </>
        );
    }
    return (
        <div className="flex items-center gap-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Upload className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <span className="flex flex-col">
                <span>
                    <span className="font-semibold text-primary">
                        Upload a file
                    </span>{' '}
                    or drag and drop
                </span>
                <span className="text-[13px] text-muted-foreground">
                    {hint}
                </span>
            </span>
        </div>
    );
}
